import { useEffect, useState } from "react";
import { useRealtime } from "@/context/RealtimeContext";

/**
 * Persistent HUD overlay: subtle grid, corner brackets, live UTC clock,
 * app tag, and the real-time link status. Rendered globally.
 */
export default function HudOverlay() {
    const [now, setNow] = useState(new Date());
    const rt = useRealtime();
    const status = rt?.status || "offline";
    const connections = rt?.connections || 0;

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const utc = now.toISOString().replace("T", " ").slice(0, 19) + "Z";
    const est = now.toLocaleTimeString("en-US", {
        timeZone: "America/New_York",
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
    // Detect DST vs EST via the short name so the label matches reality (EST / EDT).
    const estAbbrev = (() => {
        try {
            return new Intl.DateTimeFormat("en-US", {
                timeZone: "America/New_York",
                timeZoneName: "short",
            })
                .formatToParts(now)
                .find((p) => p.type === "timeZoneName")?.value || "ET";
        } catch (_) {
            return "ET";
        }
    })();

    const pill = {
        online: {
            label: connections > 1 ? `LINK LIVE · ${connections} KIOSKS` : "LINK LIVE",
            color: "#00E676",
            pulse: true,
        },
        connecting: {
            label: "RECONNECTING…",
            color: "#FFEA00",
            pulse: true,
        },
        offline: {
            label: "LINK OFFLINE",
            color: "#FF1744",
            pulse: false,
        },
    }[status];

    return (
        <div
            className="fixed inset-0 z-[60] pointer-events-none"
            aria-hidden="true"
        >
            <div className="absolute inset-0 hud-grid opacity-60" />
            {/* Corner brackets */}
            <div className="absolute top-3 left-3 w-4 h-4 border-l border-t border-virtus-orange" />
            <div className="absolute top-3 right-3 w-4 h-4 border-r border-t border-virtus-orange" />
            <div className="absolute bottom-3 left-3 w-4 h-4 border-l border-b border-virtus-orange" />
            <div className="absolute bottom-3 right-3 w-4 h-4 border-r border-b border-virtus-orange" />

            {/* Top ribbon — clock + realtime link status.
                On phones the full clock takes up too much width, so we hide
                the timestamps and keep only the compact LINK LED in the
                top-right corner. Page-specific branding lives in each page's
                own header. */}
            <div className="hidden md:flex absolute top-3 left-1/2 -translate-x-1/2 items-center gap-3 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim whitespace-nowrap">
                <span data-testid="hud-utc-time">{utc}</span>
                <span className="text-virtus-faint">·</span>
                <span data-testid="hud-est-time">
                    {est} {estAbbrev}
                </span>
                <span className="text-virtus-faint">//</span>
                <span
                    className="flex items-center gap-1"
                    data-testid={`realtime-status-${status}`}
                    title={`WebSocket: ${status}`}
                >
                    <span
                        className={`inline-block h-2 w-2 rounded-full ${pill.pulse ? "pulse-dot" : ""}`}
                        style={{ backgroundColor: pill.color }}
                    />
                    <span style={{ color: pill.color }}>{pill.label}</span>
                </span>
            </div>

            {/* Mobile-only compact LINK LED (top-center, minimal footprint). */}
            <div
                className="md:hidden absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 font-mono text-[9px] tracking-[0.22em] uppercase whitespace-nowrap"
                data-testid={`realtime-status-mobile-${status}`}
                title={`WebSocket: ${status}`}
            >
                <span
                    className={`inline-block h-1.5 w-1.5 rounded-full ${pill.pulse ? "pulse-dot" : ""}`}
                    style={{ backgroundColor: pill.color }}
                />
                <span style={{ color: pill.color }}>{status === "online" ? "LIVE" : status === "connecting" ? "SYNC" : "OFF"}</span>
            </div>
        </div>
    );
}
